import React, { FunctionComponent, ReactNode, useCallback, useEffect, useState } from 'react'
import styles from './Toaster.module.sass'
import { asyncScheduler, EMPTY, generate, Observable, Subject, throwError, zip } from 'rxjs'
import { map } from 'rxjs/operators'
import Button, { ButtonStyle } from '../Button/Button'
import { entityRemove } from '../../../utils/entity-utils'
import { getResponseErrors } from '../../../utils/utils'
import { FailedResponse } from '../../../responses/response'
import { AnimatePresence, motion } from 'framer-motion'
import { cons } from 'fp-ts/es6/Array'
import CloseIcon from 'files/close.svg'
import clsx from 'clsx'
import { Classes, Colors } from '../../../styles'

export interface Toast<T = ReactNode> {
    content: T,
    timeout?: number,
    onClick?: () => void,
}

interface InnerToast extends Omit<Toast, 'timeout'> {
    id: number,
    date: Date,
}

const subject$ = new Subject<Toast>()

const ids$ = generate(0, () => true, x => x + 1, asyncScheduler)

const pipe$ = zip(subject$, ids$).pipe(
    map(([toast, id]) => ({
        ...toast,
        id,
        date: new Date(Date.now() + (toast.timeout || 10000)),
    })),
)

type ToastData<T = ReactNode> = string | Toast<T>
const getToast = (data: ToastData<string>): Toast<string> => typeof data === 'string' ? {content: data} : data

const pushToast = (toast: Toast): void => {
    subject$.next(toast)
}

const splitLines = (text: string) => text.split('\n').map((line, index) => <div key={index}>{line}</div>)

const textToast = (intent: 'info' | 'error' , data: ToastData<string>) => {
    const toast = getToast(data)
    pushToast({
        timeout: toast.timeout,
        onClick: toast.onClick,
        content: <div className={styles[intent]}>
            {splitLines(toast.content)}
        </div>
    })
}

export const infoToast = (data: ToastData<string>): void => {
    textToast('info', data)
}

export const errorToast = (data: ToastData<string>): void => {
    textToast('error', data)
}

export const componentToast = pushToast

export const toastFailedResponse = (response: FailedResponse, defaultMessage: string): void => pushToast({
    content: <div className={styles.error}>
        {splitLines(getResponseErrors(response, defaultMessage))}
    </div>
})

export const pushErrorAndEmpty = (response: FailedResponse, defaultMessage: string): Observable<never> => {
    toastFailedResponse(response, defaultMessage)
    return EMPTY
}

export const pushErrorAndRethrow = (response: FailedResponse, defaultMessage: string): Observable<never> => {
    toastFailedResponse(response, defaultMessage)
    return throwError(response)
}

const Toaster: FunctionComponent = () => {
    const [toasts, setToasts] = useState<InnerToast[]>([])

    const filterToasts = useCallback(() => {
        setToasts(toasts => toasts.length === 0 ? toasts : toasts.filter(toast => toast.date.getTime() > Date.now()))
    }, [])

    useEffect(() => {
        const pipeSub = pipe$.subscribe(toast => setToasts(cons(toast)))
        const interval = setInterval(filterToasts, 1000)
        return () => {
            pipeSub.unsubscribe()
            clearInterval(interval)
        }
    }, [filterToasts])

    return <div className={styles.toaster}>
        <AnimatePresence>
            {
                toasts.map(toast => <motion.div
                    layout
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    key={toast.id}
                    className={clsx(styles.toast, {[Classes.pointer]: toast.onClick})}
                    onClick={() => {
                        if(toast.onClick) {
                            toast.onClick()
                            setToasts(entityRemove(toast))
                        }
                    }}
                >
                    {toast.content}
                    <Button
                        className={styles.close}
                        color={Colors.red}
                        buttonStyle={ButtonStyle.TINTED}
                        onClick={event => {
                            event.stopPropagation()
                            setToasts(entityRemove(toast))
                        }}
                    >
                        <CloseIcon/>
                    </Button>
                </motion.div>)
            }
        </AnimatePresence>
    </div>
}

export default Toaster